import React, { useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";

/**
 * Social Authentication Callback Component
 * Handles OAuth redirects and communicates with parent window
 */
const SocialAuthCallback = () => {
  const { provider } = useParams();
  const location = useLocation();

  useEffect(() => {
    console.log("🔍 SocialAuthCallback - Starting:", {
      provider,
      location: location.pathname,
      search: location.search,
      hasOpener: !!window.opener,
      origin: window.location.origin,
    });

    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");
    const error = urlParams.get("error");
    const errorDescription = urlParams.get("error_description");

    console.log("🔍 SocialAuthCallback - URL Parameters:", {
      code: code ? `${code.substring(0, 10)}...` : null,
      state: state ? `${state.substring(0, 20)}...` : null,
      error,
      errorDescription,
      allParams: Object.fromEntries(urlParams.entries()),
    });

    // Communicate with parent window
    if (window.opener) {
      console.log(
        "🔍 SocialAuthCallback - Parent window found, sending message..."
      );

      // Try to get the parent window's origin from the opener
      let parentOrigin = "*"; // Default to wildcard for safety

      try {
        // Try to detect parent origin from state parameter or use both possible origins
        const possibleOrigins = [
          "https://eventegration.com",
          "https://www.eventegration.com",
          "http://localhost:3000", // For development
        ];

        console.log(
          "🔍 SocialAuthCallback - Trying to detect parent origin..."
        );

        // Use wildcard origin for broader compatibility
        parentOrigin = "*";

        console.log("🔍 SocialAuthCallback - Using origin:", parentOrigin);
      } catch (e) {
        console.log("🔍 SocialAuthCallback - Using wildcard origin for safety");
        parentOrigin = "*";
      }

      if (error) {
        console.log("🚨 SocialAuthCallback - OAuth Error:", {
          error,
          errorDescription,
        });
        window.opener.postMessage(
          {
            type: "SOCIAL_AUTH_ERROR",
            error: errorDescription || error,
            provider,
          },
          parentOrigin
        );
      } else if (code && state) {
        console.log(
          "✅ SocialAuthCallback - OAuth Success, sending data to parent"
        );
        window.opener.postMessage(
          {
            type: "SOCIAL_AUTH_SUCCESS",
            code,
            state,
            provider,
          },
          parentOrigin
        );
      } else {
        console.log("🚨 SocialAuthCallback - Missing required parameters");
        window.opener.postMessage(
          {
            type: "SOCIAL_AUTH_ERROR",
            error: "Missing authorization code",
            provider,
          },
          parentOrigin
        );
      }

      // Close the popup
      console.log("🔍 SocialAuthCallback - Closing popup window");
      window.close();
    } else {
      console.log(
        "🚨 SocialAuthCallback - No parent window found, redirecting to home"
      );
      // If no opener (direct navigation), redirect back to main page
      window.location.href = "/";
    }
  }, [provider, location.search]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      gap={2}
    >
      <CircularProgress size={48} />
      <Typography variant="h6">Processing authentication...</Typography>
      <Typography variant="body2" color="text.secondary">
        This window will close automatically
      </Typography>
    </Box>
  );
};

export default SocialAuthCallback;
